import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Phone, FileText, LogOut, User, ArrowRight, Settings, Users, Image } from 'lucide-react';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/88797887-5fbe-4b8e-86d9-2a8b6e9a51f8/images/24255a550df67aeec175ac877a674e8bae385938fef42044bf92b3a8217b6427.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loadingEstimates, setLoadingEstimates] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchEstimates = async () => {
      if (!user) return;
      try {
        const { data } = await axios.get(`${API_URL}/api/estimates`, {
          withCredentials: true,
        });
        setEstimates(data);
      } catch (err) {
        console.error('Failed to fetch estimates:', err);
      } finally {
        setLoadingEstimates(false);
      }
    };

    fetchEstimates();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A] mx-auto"></div>
          <p className="font-jakarta text-[#475569] mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="dashboard-logo">
              <img src={LOGO_URL} alt="Essex Street Contracting" className="h-10 w-auto" />
              <div className="hidden sm:block">
                <p className="font-outfit font-bold text-[#0F172A] text-sm leading-tight">Essex Street</p>
                <p className="font-outfit font-medium text-[#0F172A] text-xs leading-tight">Contracting LLC</p>
              </div>
            </Link>

            <div className="flex items-center gap-4">
              <a href="tel:347-938-3278" className="hidden md:flex items-center gap-2 text-[#0F172A] hover:text-[#D4AF37] transition-colors" data-testid="dashboard-phone">
                <Phone className="h-4 w-4" />
                <span className="font-jakarta font-medium">347-938-3278</span>
              </a>
              <div className="flex items-center gap-2 text-[#475569]">
                <User className="h-4 w-4" />
                <span className="font-jakarta text-sm">{user.name || user.email}</span>
              </div>
              <Button 
                onClick={handleLogout}
                variant="ghost"
                className="text-[#475569] hover:text-[#0F172A]"
                data-testid="dashboard-logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0F172A]" data-testid="dashboard-welcome">
            Welcome, {user.name || 'Client'}
          </h1>
          <p className="font-jakarta text-[#475569] mt-2">
            View and manage your project estimates
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link 
            to="/estimate"
            className="bg-white border border-[#E2E8F0] rounded-sm p-6 hover:border-[#D4AF37] transition-all group"
            data-testid="view-sample-estimate-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <FileText className="h-8 w-8 text-[#D4AF37] mb-3" />
                <h3 className="font-outfit text-lg font-semibold text-[#0F172A] group-hover:text-[#D4AF37]">
                  Sample Estimate
                </h3>
                <p className="font-jakarta text-sm text-[#475569] mt-1">
                  View detailed restoration estimate
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Link>

          <a 
            href="tel:347-938-3278"
            className="bg-[#0F172A] border border-[#0F172A] rounded-sm p-6 hover:bg-[#1E293B] transition-all group"
            data-testid="contact-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <Phone className="h-8 w-8 text-[#D4AF37] mb-3" />
                <h3 className="font-outfit text-lg font-semibold text-white">
                  Contact Us
                </h3>
                <p className="font-jakarta text-sm text-gray-300 mt-1">
                  347-938-3278
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </a>

          <Link 
            to="/"
            className="bg-white border border-[#E2E8F0] rounded-sm p-6 hover:border-[#D4AF37] transition-all group"
            data-testid="back-home-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="h-8 w-8 bg-[#F1F5F9] rounded-sm flex items-center justify-center mb-3">
                  <span className="text-[#D4AF37] text-xl">←</span>
                </div>
                <h3 className="font-outfit text-lg font-semibold text-[#0F172A] group-hover:text-[#D4AF37]">
                  Back to Home
                </h3>
                <p className="font-jakarta text-sm text-[#475569] mt-1">
                  Visit main website
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Link>
        </div>

        {/* Admin Quick Actions */}
        {user.role === 'admin' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <Link 
              to="/admin"
              className="bg-[#D4AF37] border border-[#D4AF37] rounded-sm p-6 hover:bg-[#0F172A] hover:border-[#0F172A] transition-all group"
              data-testid="admin-panel-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <Settings className="h-8 w-8 text-[#0F172A] group-hover:text-[#D4AF37] mb-3" />
                  <h3 className="font-outfit text-lg font-semibold text-[#0F172A] group-hover:text-white">
                    Admin Panel
                  </h3>
                  <p className="font-jakarta text-sm text-[#0F172A]/70 group-hover:text-gray-300 mt-1">
                    Manage clients & media
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-[#0F172A] group-hover:text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </Link>

            <Link 
              to="/admin"
              className="bg-white border border-[#E2E8F0] rounded-sm p-6 hover:border-[#D4AF37] transition-all group"
              data-testid="manage-clients-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Users className="h-8 w-8 text-[#D4AF37]" />
                  <div>
                    <h3 className="font-outfit text-lg font-semibold text-[#0F172A] group-hover:text-[#D4AF37]">
                      Manage Clients
                    </h3>
                    <p className="font-jakarta text-sm text-[#475569] mt-1">
                      Add or edit client accounts
                    </p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </Link>
          </div>
        )}

        {/* Estimates List */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm">
          <div className="p-6 border-b border-[#E2E8F0]">
            <h2 className="font-outfit text-xl font-semibold text-[#0F172A]" data-testid="estimates-section-title">
              Your Estimates
            </h2>
          </div>
          
          {loadingEstimates ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F172A] mx-auto"></div>
              <p className="font-jakarta text-[#475569] mt-4">Loading estimates...</p>
            </div>
          ) : estimates.length === 0 ? (
            <div className="p-8 text-center" data-testid="no-estimates-message">
              <FileText className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
              <h3 className="font-outfit text-lg font-semibold text-[#0F172A]">No Estimates Yet</h3>
              <p className="font-jakarta text-[#475569] mt-2 mb-6">
                Contact us to request an estimate for your project.
              </p>
              <Link to="/estimate">
                <Button className="bg-[#0F172A] text-white hover:bg-[#D4AF37] hover:text-[#0F172A] font-jakarta rounded-sm" data-testid="view-sample-btn">
                  View Sample Estimate
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {estimates.map((estimate) => (
                <Link 
                  key={estimate.id}
                  to={`/estimate/${estimate.id}`}
                  className="block p-6 hover:bg-[#F8FAFC] transition-colors"
                  data-testid={`estimate-item-${estimate.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-outfit font-semibold text-[#0F172A]">{estimate.project_name}</h3>
                      <p className="font-jakarta text-sm text-[#475569]">{estimate.project_address}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-lg font-semibold text-[#0F172A]">
                        ${estimate.total_rcv?.toLocaleString() || '0'}
                      </p>
                      <span className={`inline-block px-2 py-1 rounded-sm text-xs font-jakarta ${
                        estimate.status === 'approved' ? 'bg-green-100 text-green-800' :
                        estimate.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {estimate.status}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Admin Notice */}
        {user.role === 'admin' && (
          <div className="mt-6 bg-[#D4AF37]/10 border border-[#D4AF37] rounded-sm p-4" data-testid="admin-notice">
            <p className="font-jakarta text-[#0F172A]">
              <span className="font-semibold">Admin Access:</span> You can view and manage all estimates.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
